import { useMutation, useQuery } from '@apollo/client';
import * as React from 'react';
import { createContext, useCallback, useContext, useEffect, useReducer, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { useToast } from '@serodigital/ui-components';
import {
  GetControlPeripherals,
  GetControlPeripheralsQuery,
  GetControlPeripheralsQueryVariables,
  ListTrials,
  ListTrialsQuery,
  OverrideHeating,
  QueryOperator,
  SearchBeeData,
  SearchBeeDataQuery,
  SearchBeeDataQueryVariables,
  SearchZones,
  SendPeripheralCommand,
  SendPeripheralCommandMutation,
  SendPeripheralCommandMutationVariables,
} from '../../../../../shared/api-clients/gql';
import { useDebounce } from '../../../../../shared/hooks/useDebounce';
import { RouteParams } from '../../../routes';
import {
  ACTIVE,
  BeeDetailsReducer,
  CONTROL_PERIPHERALS,
  DEVICE_INFO,
  UPDATE_ZONE_SETPOINT,
  ZONES
} from './reducer';
import { IDetailsState, ITrialDetail, SetActiveNavFunction, SetControlPeripheralState, SetSetpointFunction } from './types';

interface IDetailsProvider extends IDetailsState {
  refetchBeeDetails: () => void;
  setActiveNav: SetActiveNavFunction;
  setSetpoint: SetSetpointFunction;
  setControlPeripheralState: SetControlPeripheralState;
  trials: ITrialDetail[];
  deviceId: string;
}

export const DetailsContext = createContext(null);

export const useDetails = () => useContext<IDetailsProvider>(DetailsContext);

function generateActivePaths(pathname: string) {
  switch (true) {
    case pathname.includes('/configuration'):
      return 4;
    case pathname.includes('/settings'):
      return 3;
    case pathname.includes('/heating'):
      return 2;
    case pathname.includes('/hotwater'):
      return 1;
    default:
      return 0;
  }
}

export function DetailsProvider({ children }) {
  const { deviceId } = useParams<RouteParams>();
  const { state: locState, pathname } = useLocation();

  const debounce = useDebounce(500);
  const { setToastMessage } = useToast();
  const [state, dispatch] = useReducer(BeeDetailsReducer, {
    deviceInfo: locState ?? {},
    active: generateActivePaths(pathname),
    zones: [],
    controlPeripherals: [],
  });

  const [trials, setTrials] = useState<ITrialDetail[]>([]);

  const { data: trialsData } = useQuery<ListTrialsQuery>(ListTrials, {
    fetchPolicy: 'network-only',
  });

  const { data, refetch, loading } = useQuery<SearchBeeDataQuery, SearchBeeDataQueryVariables>(SearchBeeData, {
    variables: {
      query: {
        where: [
          {
            key: 'deviceId',
            operator: QueryOperator.Equals,
            valueOne: deviceId,
          },
        ],
      },
    },
    fetchPolicy: 'network-only',
    pollInterval: 5000,
  });

  const { data: zoneData } = useQuery(SearchZones, {
    variables: {
      deviceId,
    },
    pollInterval: 5000,
  });

  const { data: controlPeripheralsData } = useQuery<GetControlPeripheralsQuery, GetControlPeripheralsQueryVariables>(
    GetControlPeripherals,
    {
      variables: {
        deviceId,
      },
      fetchPolicy: 'network-only',
      pollInterval: 5000,
    },
  );

  const [overrideHeating] = useMutation(OverrideHeating);

  const [sendPeripheralCommand] = useMutation<SendPeripheralCommandMutation, SendPeripheralCommandMutationVariables>(
    SendPeripheralCommand,
  );

  useEffect(() => {
    if (!data) return;
    dispatch({ type: DEVICE_INFO, payload: data.searchBeeData });
  }, [data]);

  useEffect(() => {
    if (!zoneData) return;
    dispatch({ type: ZONES, payload: zoneData.searchZones.items });
  }, [zoneData]);

  useEffect(() => {
    if (!controlPeripheralsData?.getControlPeripherals?.peripherals.length) return;
    dispatch({ type: CONTROL_PERIPHERALS, payload: controlPeripheralsData.getControlPeripherals.peripherals });
  }, [controlPeripheralsData]);

  useEffect(() => {
    if (!trialsData || !data) return;
    setTrials(trialsData.listTrials.items.map((trial) => ({
      id: trial.id,
      name: trial.name
    })));
  }, [trialsData && data]);

  const refetchBeeDetails = useCallback(
    () =>
      refetch({
        query: {
          where: [
            {
              key: 'deviceId',
              operator: QueryOperator.Equals,
              valueOne: deviceId,
            },
          ],
        },
      }),
    [deviceId],
  );

  const setActiveNav = useCallback(
    (index?: number) => {
      if (!pathname) return;
      dispatch({ type: ACTIVE, payload: index ?? generateActivePaths(pathname) });
    },
    [pathname],
  );

  useEffect(() => {
    if (!loading) {
      refetchBeeDetails();
    }
    setActiveNav();
  }, [pathname]);

  const setSetpoint = useCallback((setpoint, zoneId) => {
    dispatch({ type: UPDATE_ZONE_SETPOINT, payload: { setpoint, zoneId } });
    debounce(async () => {
      await overrideHeating({
        variables: {
          beeId: deviceId,
          zoneId,
          setpoint,
        },
      });
      setToastMessage({
        label: `Successful override of heating for zone: ${zoneId}`,
        type: 'success',
        position: 'topCenter',
        timeout: 2000,
      });
    });
  }, []);

  const setControlPeripheralState = useCallback((deviceType, newState) => {
    debounce(async () => {
      await sendPeripheralCommand({
        variables: { deviceId, deviceType, requestedState: newState ? 1 : 0 },
      });
    });
  }, []);

  return (
    <DetailsContext.Provider
      value={{ ...state, deviceId, refetchBeeDetails, setActiveNav, setSetpoint, setControlPeripheralState, trials }}
    >
      {children}
    </DetailsContext.Provider>
  );
}
